import React, {
    ReactElement, ReactNode, useState,
} from "react";
import classNames from "classnames";
import {
    RiCalendarScheduleLine, RiCheckboxCircleLine, RiCheckDoubleLine, RiErrorWarningLine, RiEyeLine,
} from "@remixicon/react";
import { RiCheckLine, RiTimeLine } from "react-icons/ri";
import { stripTypename } from "@apollo/client/utilities";
import EmojiShort from "../../../../emoji-picker/emoji-short.json";

import {
    ConversationSourceType,
    TicketMessage, TicketMessageReaction, TicketMessageStatusType, TicketMessageType, TicketStatus, useAddReactionMutation, useRemoveReactionMutation,
} from "../../../../../__generated__/api";

import { ReactComponent as AgentLogo } from "../../../../../assets/img/agent-logo.svg";
import { TicketItem } from "../../../../../contexts/ticket-state";
import { Time } from "../../../../../date";
import ArticlePreview from "../../../../agent/answer-finder/ArticlePreview";
import Attachment from "../../../../attachment/Attachment";
import Avatar from "../../../../avatar/Avatar";
import CardActions from "../../../../chat/card-actions";
import ScheduledCardActions from "../../../../chat/card-actions/scheduled";
import { HtmlRenderer } from "../../../../html-render/HtmlRenderer";
import Styles from "./MessageCard.module.scss";
import Badge from "../../../../theme/badge/Badge";
import Button from "../../../../theme/button/Button";
import { SOURCE_TO_ICON } from "../../../labels/source/Source";
import TimeDisplay from "../../../../time/Time";
import Icon from "../../../../theme/icon/Icon";
import Popover from "../../../../popover/Popover";
import Text from "../../../../theme/text/Text";
import usePortal from "../../../../../contexts/PortalContext";

const STATUS_ICONS = {
    [TicketMessageStatusType.Success]: <Icon icon={<RiCheckboxCircleLine />} variant="tertiary" size="xsmall" />,
    [TicketMessageStatusType.Error]: <Icon.Alert size="xsmall" variant="primary" />,
    undefined: <Icon icon={<RiTimeLine />} size="xsmall" variant="subtle" />,
};

const LIST_ICONS = {
    [TicketMessageStatusType.Success]: <Icon icon={<RiCheckboxCircleLine />} variant="tertiary" size="xsmall" />,
    [TicketMessageStatusType.Error]: <Icon.Alert size="xsmall" variant="warning" />,
};

const FEATURE_MESSAGE_STATUS = "frontend.message.status";

interface BodyProps {
    chatId: string;
    message: TicketMessage;
    isHover: boolean;
    ticketItem: TicketItem;
    isAgentMessage: boolean;
    isEditable: boolean;
    isScheduled?: boolean;
}
function Body({
    chatId, message, isHover, ticketItem, isAgentMessage, isEditable, isScheduled,
}: BodyProps): ReactElement {
    const [addReaction] = useAddReactionMutation();
    const [removeReaction] = useRemoveReactionMutation();
    const { featureFlags } = usePortal();

    const isRegularActions = message.type === TicketMessageType.AgentMessage || message.type === TicketMessageType.UserMessage;

    const handleClick = async (reactions: TicketMessageReaction[]) => {
        const isCurrent = reactions.some(row => row.isCreatedByCurrentUser);
        const variables = {
            reaction: {
                chatId,
                messageId: message.id,
                reaction: reactions.firstElement.reaction,
            },
        };

        if (isCurrent) {
            await removeReaction({ variables });
        } else {
            await addReaction({ variables });
        }
    };

    let actions: ReactNode;
    if (isRegularActions) {
        actions = (
            <CardActions
                isAgentMessage={isAgentMessage}
                messageText={message.body?.html || ""}
                plainText={message.body.plaintext as string}
                isHover={isHover}
                messageId={message.id}
                isEditable={isEditable}
                isRedacted={message.redactedDate != null}
                isDeletable={!message.isDeleted && message.type === TicketMessageType.AgentMessage}
                ticketItem={ticketItem}
            />
        );
    } else if (isScheduled) {
        actions = (
            <ScheduledCardActions
                messageText={message.body?.html || ""}
                isHover={isHover}
                messageId={message.id}
                ticketItem={ticketItem}
                scheduledDate={message.schedule?.date}
            />
        );
    }

    const retrySend = () => {
        ticketItem.sendMessage({
            body: message.body.html as string,
            type: message.type,
            scheduledDate: message.schedule?.date,
            attachmentReferences: [],
            articles: message.articles?.map(article => stripTypename(article)),
        });
    };

    return (
        <div className={Styles["message-body"]}>
            <div className={Styles["message-text"]}>
                {!message.isDeleted && message.body?.html && (
                    <HtmlRenderer className={Styles["message-body-contents"]} html={message.body.html} />
                )}

                {message.redactedDate ? (
                    <div className={Styles["message-original"]}>
                        [Message Redacted]
                    </div>
                ) : (
                    message.originalBody && message.originalBody !== message.body.html && (
                        <small className={Styles["message-original-container"]}>
                            <div className={Styles["message-original-title"]}>
                                {message.isDeleted ? "(Deleted)" : "(Edited)"}
                                :
                            </div>

                            <HtmlRenderer
                                className={Styles["message-original"]}
                                html={message.originalBody.html as string}
                            />
                        </small>
                    )
                )}

                {(message.attachments?.isNotEmpty || message.articles?.isNotEmpty) && (
                    <div className={Styles["message-attachment-list"]}>
                        {message.attachments?.isNotEmpty && (
                            <div className={Styles["message-attachments"]}>
                                {message.attachments?.map(attach => (
                                    <Attachment
                                        className="px-2 py-2 in-message"
                                        mimeType={attach.mimeType as string}
                                        title={attach.title ?? attach.fileName as string}
                                        url={attach.url as string}
                                        key={attach.url}
                                        variant="preview"
                                    />
                                ))}
                            </div>
                        )}

                        {message.articles?.isNotEmpty && (
                            <div className={Styles["message-attachments"]}>
                                {message.articles?.map(article => (
                                    <ArticlePreview
                                        key={article.id}
                                        className={`px-2 py-2 ${Styles["reference-answer"]}`}
                                        article={article}
                                        variant="preview"
                                        isClickable
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {actions}
                {featureFlags.has(FEATURE_MESSAGE_STATUS)
                    && message.type === TicketMessageType.AgentMessage
                    && message.status?.status !== TicketMessageStatusType.Success
                    && (
                        <Popover
                            popoverClassName={Styles["message-status-popover"]}
                            buttonClassName={classNames(
                                Styles["message-status"],
                                Styles["message-status-agent"],
                                "message-status",
                            )}
                            variant="primary"
                            text={
                                STATUS_ICONS[message.status?.status || "undefined"]
                            }
                        >
                            {message.status ? (
                                <>
                                    <ul className={Styles["message-status-lines"]}>
                                        {message.status?.lines?.map(line => (
                                            <li key={line.date} className={Styles["message-status-line"]}>

                                                <div className={Styles["message-status-line-header"]}>
                                                    {LIST_ICONS[line.error
                                                        ? TicketMessageStatusType.Error
                                                        : TicketMessageStatusType.Success]}
                                                    <Text
                                                        as="span"
                                                        textVariant="body-2-semibold"
                                                        className={Styles["message-status-line-source"]}
                                                    >
                                                        {line.sourceName}
                                                    </Text>

                                                </div>

                                                <Text
                                                    as="div"
                                                    textVariant="body-2-regular"
                                                    colorVariant={line.error ? "danger" : "success"}
                                                    className={Styles["message-status-line-message"]}
                                                >
                                                    {line.error || "Success"}
                                                </Text>

                                            </li>
                                        ))}
                                    </ul>
                                    <Button
                                        onClick={retrySend}
                                        variant="outline-blue"
                                        size="small"
                                        className={Styles["message-status-button"]}
                                    >
                                        Retry Sending (without attachments)
                                    </Button>
                                </>
                            ) : (
                                <Text textVariant="body-2-regular">
                                    <Icon icon={<RiTimeLine />} />
                                    {" "}
                                    Message sending...
                                </Text>
                            )}

                        </Popover>

                    )}
            </div>

            {message.reactions?.isNotEmpty && (
                <div className={Styles["reaction-container"]}>
                    {Object.values(message.reactions.groupBy(row => row.reaction)).map(group => {
                        const first = group.firstElement;
                        const isCreatedByCurrentUser = group.some(row => row.isCreatedByCurrentUser);
                        const emoji = EmojiShort[`:${first.reaction}:` as keyof typeof EmojiShort] ?? `:${first.reaction}:`;

                        return (
                            <Button
                                className={Styles.reaction}
                                onClick={() => handleClick(group)}
                                variant="none"
                                title={isCreatedByCurrentUser ? "Added by you" : "Added by others"}
                                key={first.reaction}
                            >
                                <Badge variant="info">
                                    {emoji}
                                    <span className="px-1 ps-2">
                                        {group.length}
                                    </span>
                                </Badge>
                            </Button>
                        );
                    })}
                </div>
            )}

        </div>
    );
}

Body.defaultProps = {
    isScheduled: false,
};

export interface MessageCardProps {
    message: TicketMessage;
    previous: TicketMessage | undefined;
    isEditable: boolean;
    ticketItem: TicketItem;
}

const COMBINE_TIME_INTERVAL = 5 * Time.MINUTE;

export default function MessageCard({
    message, previous, isEditable, ticketItem,
}: MessageCardProps): ReactElement {
    const isScheduled = message.type === TicketMessageType.Scheduled;
    const isComment = message.type === TicketMessageType.Comment;
    const isAgentMessage = isScheduled || isComment
         || message.type === TicketMessageType.AgentMessage
         || message.type === TicketMessageType.Triggered;

    const date = new Date(message.createdDate);
    const previousDate = previous ? new Date(previous.createdDate) : undefined;

    const [isHover, setHover] = useState(false);

    if (!isScheduled && message.creatorId === previous?.creatorId
        && message.type === previous?.type
        && date.getTime() - (previousDate?.getTime() ?? 0) < COMBINE_TIME_INTERVAL) {
        return (
            <div
                className={classNames(
                    Styles["message-card"],
                    Styles["message-card-append"],
                    Styles[`message-card-${isAgentMessage ? "agent" : "user"}`],
                    Styles[`message-card-${message.type.toLowerCase()}`],
                )}
                onMouseOver={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                onFocus={() => setHover(true)}
            >
                <Body
                    chatId={ticketItem.id}
                    message={message}
                    isHover={isHover}
                    ticketItem={ticketItem}
                    isAgentMessage={isAgentMessage}
                    isEditable={isEditable}
                />
            </div>
        );
    }

    const avatarIcon = isAgentMessage
        ? <AgentLogo />
        : SOURCE_TO_ICON[ticketItem.chat.source?.type as ConversationSourceType ?? ConversationSourceType.ConversationGroup];

    return (
        <div
            className={classNames(
                Styles["message-card"],
                Styles[`message-card-${isAgentMessage ? "agent" : "user"}`],
                Styles[`message-card-${message.type.toLowerCase()}`],
            )}
            onMouseOver={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onFocus={() => setHover(true)}
        >
            <Avatar
                src={message.creator?.avatarUrl as string}
                variant="sm-2"
                isCircle
                statusIcon={avatarIcon}
            />

            <div className={Styles["message-card-message-container"]}>
                <div className={Styles["name-container"]}>
                    <div className={Styles["name-display"]}>
                        {message.creator?.name ?? "Agent"}
                    </div>

                    {isScheduled ? (
                        <div className={Styles.time}>
                            <RiCalendarScheduleLine className="icon-small" />
                            {" Send "}
                            <TimeDisplay date={message.schedule?.date} variant="date" />
                            {" at "}
                            <TimeDisplay date={message.schedule?.date} variant="time" />
                        </div>
                    ) : (
                        <TimeDisplay date={date} variant="time" className={Styles.time} />
                    )}

                    {isComment && (
                        <div className={Styles["comment-warning"]}>
                            <RiEyeLine />
                            {" "}
                            Only visible to agents
                        </div>
                    )}
                </div>

                <Body
                    chatId={ticketItem.id}
                    message={message}
                    isHover={isHover}
                    ticketItem={ticketItem}
                    isAgentMessage={isAgentMessage}
                    isEditable={isEditable}
                    isScheduled={isScheduled}
                />
            </div>

        </div>
    );
}
